// src/features/collaboration/components/ConnectionsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Tabs,
  Tab,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { THEME_COLORS } from '../../../styles/theme';
import { buildInviteContent, buildWhatsAppLink } from '../services/inviteLinkService';
import { sendInviteEmail } from '../services/inviteEmailService';
import {
  acceptConnectionRequest,
  cancelConnectionRequest,
  fetchConnections,
  fetchNetworkUsers,
  fetchPendingRequests,
  ignoreConnectionRequest,
  sendConnectionRequest,
  sendMessage
} from '../services/collaborationService';

const ConnectionsPage = ({ initialTab = 0 }) => {
  const { currentUser, userProfile } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [pendingIncoming, setPendingIncoming] = useState([]);
  const [pendingOutgoing, setPendingOutgoing] = useState([]);
  const [search, setSearch] = useState('');
  const [busyIds, setBusyIds] = useState(new Set());
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteWhatsApp, setInviteWhatsApp] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviteStatus, setInviteStatus] = useState(null);
  const [sendingInviteEmail, setSendingInviteEmail] = useState(false);

  const loadData = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const [people, conns, pending] = await Promise.all([
        fetchNetworkUsers(currentUser.uid),
        fetchConnections(currentUser.uid),
        fetchPendingRequests(currentUser.uid)
      ]);
      setUsers(people || []);
      setConnections(conns || []);
      setPendingIncoming(pending?.incoming || []);
      setPendingOutgoing(pending?.outgoing || []);
    } catch (error) {
      console.error('Error loading connections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const activeConnections = useMemo(
    () => (connections || []).filter((c) => c && c.status === 'active'),
    [connections]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name || '';
      return name.toLowerCase().includes(q);
    });
  }, [users, search]);

  const isConnected = (userId) =>
    activeConnections.some((c) => c.user?.id === userId);

  const hasOutgoing = (userId) =>
    pendingOutgoing.some((r) => r.toUserId === userId);

  const hasIncoming = (userId) =>
    pendingIncoming.some((r) => r.fromUserId === userId);

  const withBusy = async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await fn();
      await loadData();
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const renderUserRow = (user) => {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'User';
    const title = user.title || '';
    const busy = busyIds.has(user.id);

    let action = (
      <Button
        variant="contained"
        size="small"
        disabled={busy}
        sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
        onClick={() => withBusy(user.id, () => sendConnectionRequest(currentUser.uid, user.id))}
      >
        Connect
      </Button>
    );

    if (isConnected(user.id)) {
      action = (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setMessageRecipient({ id: user.id, name });
            setMessageText('');
          }}
        >
          Message
        </Button>
      );
    } else if (hasOutgoing(user.id)) {
      action = (
        <Button variant="outlined" size="small" disabled>
          Request Sent
        </Button>
      );
    } else if (hasIncoming(user.id)) {
      const req = pendingIncoming.find((r) => r.fromUserId === user.id);
      action = (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
            onClick={() => withBusy(user.id, () => acceptConnectionRequest(req.id))}
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => withBusy(user.id, () => ignoreConnectionRequest(req.id))}
          >
            Ignore
          </Button>
        </Box>
      );
    }

    return (
      <Box key={user.id} display="flex" alignItems="center" justifyContent="space-between" py={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar src={user.profileImage || undefined}>
            {name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {action}
      </Box>
    );
  };

  const inviterName = userProfile?.name || currentUser?.displayName || 'Teachmate';
  const baseInviteLink = `${window.location.origin}/register?ref=invite&from=${currentUser?.uid || ''}`;
  const inviteBody = buildInviteContent({
    type: 'connection',
    inviterName,
    link: baseInviteLink,
    note: inviteNote
  });
  const inviteSubject = `${inviterName} invited you to join Teachmate`;

  const emailValid = !inviteEmail || /\S+@\S+\.\S+/.test(inviteEmail);
  const whatsappValid = !inviteWhatsApp || /[0-9]/.test(inviteWhatsApp);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 2 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        My Network
      </Typography>

      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Discover" />
            <Tab label={`Requests (${pendingIncoming.length})`} />
          </Tabs>

          {tab === 0 && (
            <>
              <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Invite by Email or WhatsApp
                </Typography>
                {inviteStatus && (
                  <Alert
                    severity={inviteStatus.type}
                    sx={{ mb: 1 }}
                    onClose={() => setInviteStatus(null)}
                  >
                    {inviteStatus.message}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  size="small"
                  label="Email address (optional)"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  error={!emailValid}
                  helperText={!emailValid ? 'Enter a valid email address.' : ' '}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="WhatsApp number (optional)"
                  value={inviteWhatsApp}
                  onChange={(e) => setInviteWhatsApp(e.target.value)}
                  error={!whatsappValid}
                  helperText={!whatsappValid ? 'Enter a valid phone number.' : ' '}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Personal note (optional)"
                  value={inviteNote}
                  onChange={(e) => setInviteNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    disabled={!inviteEmail.trim() || !emailValid || sendingInviteEmail}
                    onClick={async () => {
                      if (!inviteEmail.trim() || !emailValid || sendingInviteEmail) return;
                      try {
                        setSendingInviteEmail(true);
                        await sendInviteEmail({
                          toEmail: inviteEmail.trim(),
                          subject: inviteSubject,
                          body: inviteBody,
                          type: 'connection',
                          link: baseInviteLink
                        });
                        setInviteStatus({ type: 'success', message: 'Email invite sent.' });
                      } catch (error) {
                        console.error('Error sending invite email:', error);
                        setInviteStatus({ type: 'error', message: error.message || 'Failed to send email invite.' });
                      } finally {
                        setSendingInviteEmail(false);
                      }
                    }}
                  >
                    {sendingInviteEmail ? 'Sending...' : 'Send Email Invite'}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={!inviteWhatsApp.trim() || !whatsappValid}
                    onClick={() => {
                      const url = buildWhatsAppLink(inviteWhatsApp, inviteBody);
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Send WhatsApp Invite
                  </Button>
                </Box>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Search people by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2 }}
              />

              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: THEME_COLORS.primary }} />
                </Box>
              ) : (
                <>
                  {filteredUsers.length === 0 ? (
                    <Typography color="textSecondary">No users found.</Typography>
                  ) : (
                    filteredUsers.map((u, idx) => (
                      <React.Fragment key={u.id}>
                        {renderUserRow(u)}
                        {idx < filteredUsers.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}
                </>
              )}
            </>
          )}

          {tab === 1 && (
            <>
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: THEME_COLORS.primary }} />
                </Box>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Incoming
                  </Typography>
                  {pendingIncoming.length === 0 ? (
                    <Typography color="textSecondary" sx={{ mb: 2 }}>
                      No incoming requests.
                    </Typography>
                  ) : (
                    pendingIncoming.map((req, idx) => (
                      <React.Fragment key={req.id}>
                        {renderUserRow(req.user)}
                        {idx < pendingIncoming.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Sent
                  </Typography>
                  {pendingOutgoing.length === 0 ? (
                    <Typography color="textSecondary">No sent requests.</Typography>
                  ) : (
                    pendingOutgoing.map((req, idx) => (
                      <React.Fragment key={req.id}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar src={req.user?.profileImage || undefined}>
                              {(req.user?.name || 'U').charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {req.user?.name || 'User'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {req.user?.title || ''}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={busyIds.has(req.id)}
                            onClick={() => withBusy(req.id, () => cancelConnectionRequest(req.id))}
                          >
                            Cancel
                          </Button>
                        </Box>
                        {idx < pendingOutgoing.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(messageRecipient)} onClose={() => setMessageRecipient(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {messageRecipient ? `Message ${messageRecipient.name}` : 'Message'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Write a message..."
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageRecipient(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={sendingMessage || !messageText.trim() || !messageRecipient}
            sx={{ backgroundColor: THEME_COLORS.primary, '&:hover': { backgroundColor: '#1e1c4e' } }}
            onClick={async () => {
              if (!currentUser || !messageRecipient) return;
              setSendingMessage(true);
              try {
                await sendMessage(currentUser.uid, messageRecipient.id, messageText.trim());
                setMessageRecipient(null);
                setMessageText('');
              } catch (error) {
                console.error('Error sending message:', error);
              } finally {
                setSendingMessage(false);
              }
            }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectionsPage;
